import React from "react";
import { Box, Typography } from "@mui/material";
import { PublishedCard } from "../../types";
import CoverImageExperience from "../experience/CoverImageExperience";
import AboutExperience from "../experience/AboutExperience";
import BonusExperience from "../experience/BonusExperience";
import TextExperience from "../experience/TextExperience";
import AudioExperience from "../experience/AudioExperience";
import VideoExperience from "../experience/VideoExperience";
import ImageGridExperience from "../experience/ImageGridExperience";
import InstrumentExperience from "../experience/InstrumentExperience";
import SongsExperience from "../experience/SongsExperience";
import PostcardExperience from "../experience/PostcardExperience";
import YoutubeExperience from "../experience/YoutubeExperience";
import NewsExperience from "../experience/NewsExperience";

import { mapContentToExperienceData, PageData } from "../../utils/useTemplatePageHelpers";
import { CreateCardTranslate } from "../../utils/createCardTransformShared";
import { ExperienceMomentVariant } from "../experience/ExperienceUI";

/* Renders the correct editor for a single template page.
 * It chooses the right experience component from the page type, prepares the data
 * for that component, and forwards user edits back to the modal state.
 */
interface UseTemplatePageRendererProps {
  page: PageData;
  pageIndex: number;
  darkMode: boolean;
  card: PublishedCard | null;
  usesEndingImage: boolean;
  isUpdatingRef: React.MutableRefObject<boolean>;
  updatePageData: (pageIndex: number, newContent: any) => void;
  t: CreateCardTranslate;
  momentVariant?: ExperienceMomentVariant;
}

const UseTemplatePageRenderer: React.FC<UseTemplatePageRendererProps> = ({
  page,
  pageIndex,
  darkMode,
  card,
  usesEndingImage,
  isUpdatingRef,
  updatePageData,
  t,
  momentVariant = "standard",
}) => {
  const pageType = page.type || "";
  const normalizedType = pageType.replace(/_/g, "").toLowerCase().trim();
  const experienceData = mapContentToExperienceData({
    page,
    card,
    usesEndingImage,
    t,
  });

  if (
    normalizedType === "coverimage" ||
    normalizedType === "cover_image" ||
    normalizedType === "endingimage" ||
    pageType === "CoverImage" ||
    pageType === "EndingImage" ||
    pageIndex === 0
  ) {
    return (
      <Box
        sx={{
          width: "100%",
          minHeight: "300px",
          height: "400px",
          position: "relative",
        }}
      >
        <CoverImageExperience
          darkMode={darkMode}
          data={experienceData}
          template={card?.template}
          pageType={pageType}
          onDataChange={(newData: any) => updatePageData(pageIndex, newData)}
        />
      </Box>
    );
  }

  if (normalizedType === "text" || pageType === "Text") {
    return (
      <TextExperience
        darkMode={darkMode}
        data={experienceData}
        momentVariant={momentVariant}
        onDataChange={(newData: any) => updatePageData(pageIndex, newData)}
      />
    );
  }

  if (normalizedType === "about" || pageType === "About") {
    return (
      <AboutExperience
        darkMode={darkMode}
        data={experienceData}
        momentVariant={momentVariant}
        onDataChange={(newData: any) => updatePageData(pageIndex, newData)}
      />
    );
  }

  if (normalizedType === "bonus" || pageType === "Bonus") {
    return (
      <BonusExperience
        darkMode={darkMode}
        data={experienceData}
        momentVariant={momentVariant}
        onDataChange={(newData: any) => updatePageData(pageIndex, newData)}
      />
    );
  }

  if (normalizedType === "instrument" || pageType === "Instrument") {
    return (
      <InstrumentExperience
        darkMode={darkMode}
        data={experienceData}
        momentVariant={momentVariant}
        onDataChange={(newData: any) => updatePageData(pageIndex, newData)}
      />
    );
  }

  if (normalizedType === "image" || pageType === "Image") {
    return (
      <ImageGridExperience
        darkMode={darkMode}
        data={experienceData}
        momentVariant={momentVariant}
        onDataChange={(newData: any) => {
          if (isUpdatingRef.current) return;

          const images = (newData.images || [])
            .slice(0, 8)
            .filter((img: any) => img && img.trim());

          updatePageData(pageIndex, {
            images,
            description: newData.description || "",
            url: images,
          });
        }}
      />
    );
  }

  if (normalizedType === "audio" || pageType === "Audio") {
    return (
      <AudioExperience
        key={`audio-page-${page.id ?? pageIndex}-${page.order}`}
        darkMode={darkMode}
        template={card?.template}
        data={experienceData}
        momentVariant={momentVariant}
        onDataChange={(newData: any) =>
          updatePageData(pageIndex, {
            audioUrl: newData.audioUrl || "",
            url: newData.audioUrl || "",
            description: newData.description || "",
            image_url: newData.image_url || "",
            ...(usesEndingImage && {
              songTitle: newData.songTitle || "",
              artistName: newData.artistName || "",
              lyrics_text: newData.lyrics_text || "",
            }),
          })
        }
      />
    );
  }

  if (normalizedType === "video" || pageType === "Video") {
    const giftCardTemplate = t("gift_card_template", { ns: "taascard" });
    const isGiftCardTemplate = card?.template === giftCardTemplate;
    const isPage3 = pageIndex === 2;
    const hasVideoUrl =
      experienceData?.videoUrl && experienceData.videoUrl.trim() !== "";

    if (isGiftCardTemplate && isPage3 && !hasVideoUrl) {
      return (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 4,
            py: 6,
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: "0.875rem", sm: "0.9375rem", md: "1rem" },
              fontWeight: 400,
              color: darkMode ? "var(--neutral-400)" : "var(--neutral-500)",
              textAlign: "center",
              lineHeight: 1.6,
              maxWidth: "600px",
            }}
          >
            {t(
              "this_section_will_display_the_video_content_requested_by_users",
              { ns: "taascard" }
            ) ||
              "This section will display the video content requested by users"}
          </Typography>
        </Box>
      );
    }

    return (
      <VideoExperience
        darkMode={darkMode}
        data={experienceData}
        momentVariant={momentVariant}
        onDataChange={(newData: any) =>
          updatePageData(pageIndex, {
            videoUrl: newData.videoUrl || "",
            url: newData.videoUrl || "",
            video_url: newData.videoUrl || "",
            description: newData.description || "",
          })
        }
      />
    );
  }

  if (
    normalizedType === "imagegrid" ||
    normalizedType === "image_grid" ||
    pageType === "ImageGrid"
  ) {
    return (
      <ImageGridExperience
        darkMode={darkMode}
        data={experienceData}
        momentVariant={momentVariant}
        onDataChange={(newData: any) => {
          if (isUpdatingRef.current) return;

          const images = (newData.images || [])
            .slice(0, 8)
            .filter((img: any) => img && img.trim());

          updatePageData(pageIndex, {
            images,
            description: newData.description || "",
            url: images,
          });
        }}
      />
    );
  }

  if (normalizedType === "songs" || pageType === "Songs") {
    return (
      <SongsExperience
        darkMode={darkMode}
        data={experienceData}
        onDataChange={(newData: any) => updatePageData(pageIndex, newData)}
      />
    );
  }

  if (
    normalizedType === "postcard" ||
    normalizedType === "photocard" ||
    pageType === "PostCard" ||
    pageType === "Post_Card" ||
    pageType === "Photocard" ||
    pageType === "PhotoCard"
  ) {
    return (
      <PostcardExperience
        darkMode={darkMode}
        data={experienceData}
        momentVariant={momentVariant}
        onDataChange={(newData: any) => updatePageData(pageIndex, newData)}
      />
    );
  }

  if (normalizedType === "youtube" || pageType === "Youtube") {
    return (
      <YoutubeExperience
        key={`youtube-page-${page.id ?? pageIndex}-${page.order}`}
        darkMode={darkMode}
        data={experienceData}
        momentVariant={momentVariant}
        onDataChange={(newData: any) => updatePageData(pageIndex, newData)}
      />
    );
  }

  if (normalizedType === "news" || pageType === "News") {
    return (
      <NewsExperience
        darkMode={darkMode}
        data={experienceData}
        momentVariant={momentVariant}
        onDataChange={(newData: any) => updatePageData(pageIndex, newData)}
      />
    );
  }

  return (
    <Box>
      <Typography
        variant="body2"
        sx={{
          color: darkMode ? "var(--neutral-400)" : "var(--neutral-600)",
          fontStyle: "italic",
          mb: 2,
        }}
      >
        Unknown page type: {pageType}
      </Typography>
    </Box>
  );
};

export default UseTemplatePageRenderer;
